import { DateTime } from "luxon"
import { createSelector } from "@reduxjs/toolkit"
import { asyncAction } from "../common"
import { limsRequest } from "./limsRequest"

// Register of consumables (F-B3, SPEC-005 SP-B; PRD-003 Area 3). Originally a reagent /
// reference-standard register, now generalised to every kind of consumable a QC run uses —
// reagents, reference standards, columns and volumetric glassware (kolber). Each record
// carries an identity plus the field(s) that drive its validity so result entry (F-C2) can
// record *which* item was used and flag expired / used-up material. Persisted via the real
// /lims/reagents endpoints (the path keeps the historical "reagents" name; the backend table is
// "consumables" with the type discriminator). See lims-api-contract.md.
//
// Data shape: { id, name, type, lotNumber, expiryDate, serial, installDate, maxRuns,
//   runsUsed, nominalVolume, volumeUnit, calibrationDate, calibrationDue } — only the fields
// CONSUMABLE_TYPE_FIELDS lists for a given type are shown/edited; the rest stay empty.
//
// This is lab inventory, not an authorisation: any LIMS user may edit it (no planner/admin
// gate). Every create/edit/remove/swap also writes an audit entry (F-A4).

// The kinds of consumable the register tracks. A legacy record with no `type` reads as
// "reagent". Adding a 5th kind is a CONSUMABLE_TYPE_FIELDS data edit, not a code rewrite.
export const CONSUMABLE_TYPES = ["reagent", "standard", "column", "glassware"]

// Reference-standard enums (SPEC-015 / PRD-005 Area 1). Each value maps to an i18n key
// (e.g. "primary" -> limsStandardClassPrimary) and is offered in the register's dropdowns.
//   - standardClass: the type-chain class of the standard (guide ch. 3).
//   - lifecycleStatus: the MANUAL administrative state. "expired" is NOT here — expiry is
//     derived from expiryDate (computed), never stored, so it can't go stale.
//   - storageCondition: how the standard is stored.
// A legacy standard record carries none of these; it reads forward with empty fields and a
// blank lifecycleStatus is treated as "approved" by standardStatus (see below).
export const STANDARD_CLASSES = ["primary", "secondary", "in-house"]
export const STANDARD_LIFECYCLE_STATUSES = ["quarantine", "approved", "discarded"]
export const STORAGE_CONDITIONS = ["ambient", "refrigerated", "frozen", "desiccated"]

// Per-type field descriptor that drives the form + table generically (SPEC-009). Each entry:
//   - labelKey: i18n key for the type tab / option label.
//   - fields: ordered list of { key, labelKey, kind, placeholderKey?, required?, options?,
//     optionLabelKey? } the add/edit form renders and the table shows. `kind` ∈ "text" |
//     "date" | "number" | "select". A "select" field carries `options` (an enum array) and
//     `optionLabelKey(value)` → i18n key for each option label.
//   - validity: which field(s) drive "active vs expired/overdue" (see validityStatus):
//       { kind: "expiry", field } | { kind: "calibration", field }
//       | { kind: "expiryOrRuns", dateField, runsField, maxField }
//     A type with no `validity` (none currently) is always active.
export const CONSUMABLE_TYPE_FIELDS = {
  reagent: {
    labelKey: "limsReagentTypeReagent",
    fields: [
      { key: "lotNumber", labelKey: "limsReagentLot", kind: "text", placeholderKey: "limsReagentLotPlaceholder", required: true },
      { key: "expiryDate", labelKey: "limsReagentExpiry", kind: "date", required: true },
    ],
    validity: { kind: "expiry", field: "expiryDate" },
  },
  // Reference standard (SPEC-015 / PRD-005 Area 1) — a GMP-correct entity carried IN PLACE on
  // the consumables "standard" type (no new entity): identity + lot + expiry, plus purity%,
  // water content%, type-chain class, opened date, storage condition, CoA reference and a
  // manual lifecycle status. "Expired" stays computed from expiryDate; standardStatus() folds
  // the manual lifecycle status + the date into one of approved/quarantine/expired/discarded.
  standard: {
    labelKey: "limsReagentTypeStandard",
    fields: [
      { key: "lotNumber", labelKey: "limsReagentLot", kind: "text", placeholderKey: "limsReagentLotPlaceholder", required: true },
      { key: "expiryDate", labelKey: "limsReagentExpiry", kind: "date", required: true },
      { key: "purityPercent", labelKey: "limsStandardPurity", kind: "number", placeholderKey: "limsStandardPurityPlaceholder" },
      { key: "waterContentPercent", labelKey: "limsStandardWaterContent", kind: "number", placeholderKey: "limsStandardWaterContentPlaceholder" },
      { key: "standardClass", labelKey: "limsStandardClass", kind: "select", options: STANDARD_CLASSES, optionLabelKey: standardClassLabelKey },
      { key: "lifecycleStatus", labelKey: "limsStandardStatus", kind: "select", options: STANDARD_LIFECYCLE_STATUSES, optionLabelKey: standardLifecycleLabelKey },
      { key: "openedDate", labelKey: "limsStandardOpenedDate", kind: "date" },
      { key: "storageCondition", labelKey: "limsStandardStorage", kind: "select", options: STORAGE_CONDITIONS, optionLabelKey: storageConditionLabelKey },
      { key: "coaReference", labelKey: "limsStandardCoa", kind: "text", placeholderKey: "limsStandardCoaPlaceholder" },
    ],
    validity: { kind: "expiry", field: "expiryDate" },
  },
  column: {
    labelKey: "limsConsumableTypeColumn",
    fields: [
      { key: "serial", labelKey: "limsConsumableSerial", kind: "text", placeholderKey: "limsConsumableSerialPlaceholder", required: true },
      { key: "installDate", labelKey: "limsConsumableInstallDate", kind: "date" },
      // R26 (#4090): both REQUIRED. They are the only two inputs to the `expiryOrRuns` validity
      // rule below, so a column saved with both blank is judged valid FOREVER — it can never
      // expire by date and can never reach a run limit, and nothing on the result-entry screen
      // will ever warn about it. `runsUsed` stays optional: it is the counter, not a limit, and
      // the ledger (SPEC-056 R28) maintains it from here on.
      { key: "expiryDate", labelKey: "limsReagentExpiry", kind: "date", required: true },
      { key: "maxRuns", labelKey: "limsConsumableMaxRuns", kind: "number", required: true },
      { key: "runsUsed", labelKey: "limsConsumableRunsUsed", kind: "number" },
    ],
    validity: { kind: "expiryOrRuns", dateField: "expiryDate", runsField: "runsUsed", maxField: "maxRuns" },
  },
  glassware: {
    labelKey: "limsConsumableTypeGlassware",
    fields: [
      { key: "serial", labelKey: "limsConsumableSerial", kind: "text", placeholderKey: "limsConsumableSerialPlaceholder" },
      { key: "nominalVolume", labelKey: "limsConsumableNominalVolume", kind: "number" },
      { key: "volumeUnit", labelKey: "limsConsumableVolumeUnit", kind: "text", placeholderKey: "limsConsumableVolumeUnitPlaceholder" },
      { key: "calibrationDate", labelKey: "limsConsumableCalibrationDate", kind: "date" },
      { key: "calibrationDue", labelKey: "limsConsumableCalibrationDue", kind: "date" },
    ],
    validity: { kind: "calibration", field: "calibrationDue" },
  },
}

// The persisted type of a record, defaulting a legacy/blank type to "reagent" so historical
// records read forward losslessly.
export function consumableType(consumable) {
  const type = consumable && consumable.type
  return CONSUMABLE_TYPES.includes(type) ? type : "reagent"
}

const initialState = { items: [], isLoading: false }

function sortByName(items) {
  return [...items].sort((a, b) => (a.name || "").localeCompare(b.name || ""))
}

export function reducer(state = initialState, action) {
  switch (action.type) {
    case "limsConsumables/fetch/BEGIN":
      return { ...state, isLoading: true }
    case "limsConsumables/fetch":
      return { items: sortByName(action.res), isLoading: false }
    case "limsConsumables/create":
      return { ...state, items: sortByName([...state.items, action.res]) }
    case "limsConsumables/edit":
      return { ...state, items: sortByName(state.items.map((x) => (x.id === action.res.id ? action.res : x))) }
    case "limsConsumables/remove":
      return { ...state, items: state.items.filter((x) => x.id !== action.res.id) }
    case "auth/logout":
      return initialState
    default:
      return state
  }
}

export const fetch = asyncAction("limsConsumables/fetch", (_dispatch) => {
  return limsRequest("GET", "/lims/reagents").then((res) => res.items)
})

export const create = asyncAction("limsConsumables/create", (_dispatch, consumable) => {
  return limsRequest("POST", "/lims/reagents", { input: consumable }).then((res) => res.item)
})

export const edit = asyncAction("limsConsumables/edit", (_dispatch, id, patch) => {
  return limsRequest("PATCH", `/lims/reagents/${id}`, { patch }).then((res) => res.item)
})

export const remove = asyncAction("limsConsumables/remove", (_dispatch, id) => {
  return limsRequest("DELETE", `/lims/reagents/${id}`).then((res) => ({ id: res.id }))
})

// Set a reference standard's MANUAL lifecycle status (quarantine / approved / discarded) with a
// stated reason (SPEC-015). Patches the record (status is manual; "expired" is never written —
// it's derived); the `reason` rides at the request-body TOP LEVEL so the server writes the
// `limsAuditStandardStatusChanged` audit entry with it (SPEC-021 m3). The computed
// standardStatus() folds the status with the expiry date at read time.
export const setStandardStatus = asyncAction("limsConsumables/setStandardStatus", (_dispatch, id, status, reason = "") => {
  return limsRequest("PATCH", `/lims/reagents/${id}`, {
    patch: { lifecycleStatus: status },
    reason: reason || undefined,
  }).then((res) => res.item)
})

// Whether a consumable's lot has expired relative to today (local time), by date only. A
// record with no expiry date is treated as not expired. Kept as a thin back-compat alias of
// the date check; per-type validity (incl. calibration + run counts) lives in validityStatus.
export function isExpired(consumable, now = DateTime.now()) {
  if (!consumable || !consumable.expiryDate) return false
  const expiry = DateTime.fromISO(consumable.expiryDate)
  if (!expiry.isValid) return false
  return expiry.startOf("day") < now.startOf("day")
}

// Whether a yyyy-MM-dd date field is in the past relative to today (local time). Missing /
// invalid dates are NOT in the past (so an absent validity date keeps the item active).
function isDatePast(value, now) {
  if (!value) return false
  const dt = DateTime.fromISO(value)
  if (!dt.isValid) return false
  return dt.startOf("day") < now.startOf("day")
}

// Per-type validity computed AT READ TIME, never stored (so it stays correct as dates/usage
// change). Returns one of "active" | "expired" | "overdue":
//   - reagent: "expired" when expiryDate is in the past.
//   - standard: "expired" when expiryDate is in the past OR lifecycleStatus === "discarded"
//     (a discarded standard must not be offered for use). Quarantine stays "active" (it can be
//     used with knowledge) but standardStatus() flags it separately.
//   - column: "expired" when expiryDate is in the past OR runsUsed >= maxRuns (>0).
//   - glassware: "overdue" when calibrationDue is in the past (calibration lapsed).
//   - a record whose validity field(s) are absent is "active".
// Consumers treat any non-"active" status as a SOFT warning EXCEPT the reference-standard
// hard gate at send-to-review (isStandardBlocking), which is a true block with e-signed
// override (SPEC-015).
export function validityStatus(consumable, now = DateTime.now()) {
  const type = consumableType(consumable)
  const validity = CONSUMABLE_TYPE_FIELDS[type] && CONSUMABLE_TYPE_FIELDS[type].validity
  if (!consumable || !validity) return "active"
  switch (validity.kind) {
    case "expiry":
      // A discarded reference standard is excluded just like an expired one.
      if (type === "standard" && consumable.lifecycleStatus === "discarded") return "expired"
      return isDatePast(consumable[validity.field], now) ? "expired" : "active"
    case "calibration":
      return isDatePast(consumable[validity.field], now) ? "overdue" : "active"
    case "expiryOrRuns": {
      if (isDatePast(consumable[validity.dateField], now)) return "expired"
      const max = Number(consumable[validity.maxField])
      const used = Number(consumable[validity.runsField])
      if (Number.isFinite(max) && max > 0 && Number.isFinite(used) && used >= max) return "expired"
      return "active"
    }
    default:
      return "active"
  }
}

// True when the consumable is usable (validityStatus === "active").
export function isConsumableValid(consumable, now = DateTime.now()) {
  return validityStatus(consumable, now) === "active"
}

// --- Reference standards (SPEC-015 / PRD-005 Area 1) ----------------------------------------

// i18n key for a reference-standard enum value. Declared as hoisted functions so the
// CONSUMABLE_TYPE_FIELDS.standard descriptor above can reference them.
export function standardClassLabelKey(value) {
  if (value === "primary") return "limsStandardClassPrimary"
  if (value === "secondary") return "limsStandardClassSecondary"
  if (value === "in-house") return "limsStandardClassInHouse"
  return ""
}
export function standardLifecycleLabelKey(value) {
  if (value === "approved") return "limsStandardStatusApproved"
  if (value === "quarantine") return "limsStandardStatusQuarantine"
  if (value === "discarded") return "limsStandardStatusDiscarded"
  return ""
}
export function storageConditionLabelKey(value) {
  if (value === "ambient") return "limsStorageAmbient"
  if (value === "refrigerated") return "limsStorageRefrigerated"
  if (value === "frozen") return "limsStorageFrozen"
  if (value === "desiccated") return "limsStorageDesiccated"
  return ""
}

// Derived, never-stored status of a reference standard: one of
// "approved" | "quarantine" | "expired" | "discarded". Folds the MANUAL lifecycleStatus with
// the FACTUAL expiry date so it can't go stale:
//   - manual "discarded" wins (a discarded standard is discarded regardless of date);
//   - else an expiry date in the past makes it "expired" (computed);
//   - else the manual lifecycleStatus ("quarantine" / "approved"), defaulting a blank/legacy
//     status to "approved".
// Only meaningful for type "standard"; other types return "approved" (no standard lifecycle).
export function standardStatus(consumable, now = DateTime.now()) {
  if (!consumable || consumableType(consumable) !== "standard") return "approved"
  if (consumable.lifecycleStatus === "discarded") return "discarded"
  if (isDatePast(consumable.expiryDate, now)) return "expired"
  if (consumable.lifecycleStatus === "quarantine") return "quarantine"
  return "approved"
}

// True when a reference standard must HARD-BLOCK send-to-review (expired or discarded). The
// gate decision is computed locally with no network dependency. Quarantine is NOT blocking.
export function isStandardBlocking(consumable, now = DateTime.now()) {
  const status = standardStatus(consumable, now)
  return status === "expired" || status === "discarded"
}

// True when an approved/quarantine standard's expiry date is within `days` (default 30) of
// today (and not already past). Drives the soft "expiring soon" warning (never a block).
export function isStandardExpiringSoon(consumable, now = DateTime.now(), days = 30) {
  if (!consumable || consumableType(consumable) !== "standard") return false
  if (!consumable.expiryDate) return false
  const expiry = DateTime.fromISO(consumable.expiryDate)
  if (!expiry.isValid) return false
  const daysLeft = expiry.startOf("day").diff(now.startOf("day"), "days").days
  return daysLeft >= 0 && daysLeft <= days
}

export const selectConsumables = (state) => state.limsConsumables.items
export const selectConsumablesByType = (state, type) =>
  state.limsConsumables.items.filter((c) => consumableType(c) === type)

// Consumables that are still valid (not expired/overdue/used-up) of EVERY type — what F-C2
// (result entry) offers for selection on the analysis run.
export const selectActiveConsumables = createSelector(
  [(state) => state.limsConsumables.items],
  (items) => items.filter((c) => isConsumableValid(c))
)

// Reference-standard selectors (SPEC-015). selectStandardPurity returns the purity% as a
// number (or null) for the calculation engine (Area 2) to auto-fill from the chosen standard —
// an ADDITIVE read-only link; the engine pulls, this slice never pushes into result input.
export const selectStandards = (state) =>
  state.limsConsumables.items.filter((c) => consumableType(c) === "standard")
export const selectStandardById = (state, id) =>
  state.limsConsumables.items.find((c) => c.id === id && consumableType(c) === "standard") || null
export const selectStandardPurity = (state, id) => {
  const standard = selectStandardById(state, id)
  if (!standard) return null
  const purity = Number(standard.purityPercent)
  return Number.isFinite(purity) ? purity : null
}
